<template>
    <Head title="Realtime Role Play Practice" />
    <div class="relative h-dvh w-full flex flex-col items-center justify-center background">
        <BaseButton theme="primary" class="absolute right-4 top-4 z-10" @click="handleEndRolePlay">Create Role Play Recap</BaseButton>
        <RealTimeChatMarble class="w-64 sm:w-80 md:w-96" :level="audioLevel" :state="marbleState" />

        <!-- Notifications (fixed at bottom, not draggable) -->
        <div class="absolute bottom-4 left-1/2 transform -translate-x-1/2 w-[90%] max-w-md flex flex-col items-center gap-3">
            <Transition name="slide-down">
                <BaseNotification
                    v-if="showTimeWarning"
                    key="time-warning"
                    theme="error"
                    text="Your session will end in 5 minutes"
                    class="w-full"
                    @dismiss="showTimeWarning = false" />

                <BaseNotification v-else-if="error" theme="error" :text="error" class="w-full" @dismiss="error = null" />
            </Transition>

            <Transition name="slide-down">
                <BaseNotification
                    v-if="showSessionStarted"
                    key="session-started"
                    theme="info"
                    text="Role play started, speak or interrupt anytime."
                    class="w-full"
                    @dismiss="showSessionStarted = false" />
            </Transition>

            <Transition name="slide-down">
                <div v-if="isPaused" class="text-center">
                    <p class="body-small font-semibold text-valence-grey-800">Session Paused</p>
                    <p class="details text-valence-grey-600">The session timer continues while paused</p>
                </div>
            </Transition>

            <Disclaimer :message="disclaimer_message" />
        </div>

        <!-- Draggable floating controls (only rendered when at least one control flag is on) -->
        <!-- eslint-disable-next-line vuejs-accessibility/no-static-element-interactions -->
        <div
            v-if="pause_enabled || style_adjustments_enabled"
            ref="controlsEl"
            role="toolbar"
            aria-label="Session controls"
            class="floating-controls flex items-center gap-3 rounded-2xl bg-white/90 backdrop-blur-sm px-3 py-2 shadow-lg select-none"
            :class="[controlsDragging ? 'cursor-grabbing' : 'cursor-grab', controlsPositioned ? '' : 'absolute left-1/2 -translate-x-1/2 bottom-20 sm:bottom-auto sm:top-4']"
            :style="controlsStyle"
            @mousedown="onControlsPointerDown"
            @touchstart="onControlsPointerDown">
            <!-- Drag handle indicator -->
            <i class="bi bi-grip-vertical text-valence-grey-400 text-base" aria-hidden="true"></i>

            <!-- Pause / Resume icon button (behind feature flag) -->
            <button
                v-if="pause_enabled"
                type="button"
                :aria-label="isPaused ? 'Resume' : 'Pause'"
                :disabled="isProcessingResponse"
                class="flex items-center justify-center rounded-full w-10 h-10 transition-colors disabled:opacity-40 disabled:cursor-not-allowed"
                :class="isPaused ? 'bg-valence-primary-50 text-valence-primary-500 hover:bg-valence-primary-100' : 'hover:bg-valence-grey-100 text-valence-grey-800'"
                @click="togglePause">
                <i :class="['bi text-lg', isPaused ? 'bi-play-fill' : 'bi-pause-fill']" aria-hidden="true"></i>
            </button>

            <!-- Style adjustment selector (behind feature flag) -->
            <BasePopover v-if="style_adjustments_enabled" v-model:open="isStyleMenuOpen" side="bottom" :side-offset="8">
                <template #trigger>
                    <button
                        type="button"
                        class="flex items-center gap-2 rounded-xl px-3 py-2 body-small transition-colors hover:bg-valence-grey-100 text-valence-grey-800 min-h-[44px] text-left">
                        <div class="flex flex-col">
                            <span class="details font-semibold text-valence-grey-600">Role Play Style:</span>
                            <span class="body-small text-valence-grey-800 line-clamp-1">{{ currentStyleLabel }}</span>
                        </div>
                        <i :class="['bi text-sm text-valence-grey-400', isStyleMenuOpen ? 'bi-chevron-up' : 'bi-chevron-down']" aria-hidden="true"></i>
                    </button>
                </template>
                <template #content>
                    <div class="p-2 min-w-[220px]">
                        <button
                            v-for="option of role_play_adjustments"
                            :key="option[0]"
                            type="button"
                            class="flex items-center justify-between w-full rounded-lg px-3 py-2.5 body-small transition-all hover:bg-valence-grey-50 min-h-[44px]"
                            :class="[currentStyle === option[0] ? 'text-valence-primary-500 font-semibold' : 'text-valence-grey-800']"
                            @click="handleStyleChange(option[0])">
                            {{ option[1] }}
                            <i v-if="currentStyle === option[0]" class="bi bi-check-lg text-valence-primary-500" aria-hidden="true"></i>
                        </button>
                    </div>
                </template>
            </BasePopover>
        </div>
    </div>

    <!-- Session End Dialog -->
    <BaseDialog v-model:open="showSessionEndDialog" :disable-close-on-backdrop-click="true" title="Live chat session ended">
        <div class="flex flex-col items-center text-center rounded-xl py-10 px-6 w-full md:max-w-[500px] gap-6 border-2 border-[#F5F5F5] shadow-lg bg-white">
            <div class="w-32 h-32">
                <RealTimeChatMarble class="w-full h-full" :level="0" state="listening" />
            </div>
            <h3 class="title-subsection">Your live chat session has ended</h3>
            <p class="body-small text-valence-grey-750">Thank you for your conversation! Your session has reached its time limit.</p>
            <div class="w-full flex flex-col items-center gap-4 mt-6">
                <BaseButton class="w-2/5" theme="primary" @click="handleSessionEndDialogClose"> Role Play Recap </BaseButton>
            </div>
        </div>
    </BaseDialog>
</template>

<script setup>
import { getCookie } from "/js/utils";
import { Head, router } from "@inertiajs/vue3";
import jwtToken from "~vue/api/token";
import BaseButton from "~vue/components/BaseButton.vue";
import BaseDialog from "~vue/components/BaseDialog.vue";
import BaseNotification from "~vue/components/BaseNotification.vue";
import BasePopover from "~vue/components/BasePopover.vue";
import RealTimeChatMarble from "~vue/components/RealTimeChatMarble.vue";
import { useDraggable } from "~vue/composables/useDraggable";
import { useMicConsent } from "~vue/composables/useMicConsent";
import { useRealtimeVoice } from "~vue/composables/useRealtimeVoice";
import Disclaimer from "~vue/Disclaimer.vue";
import SocketClient from "~vue/SocketClient.js";
import { useSessionTimer } from "~vue/useSessionTimer.js";
import { createWebSocketErrorHandler, logError, logUserInteraction } from "~vue/utils/logUtils";
import { computed, onMounted, onUnmounted, ref } from "vue";

const { ws_url, role_play, cs_id, cs_uuid, pause_enabled, style_adjustments_enabled, role_play_adjustments, role_play_personality, coach_api_realtime_enabled } = defineProps({
    ws_url: { type: String, required: true },
    role_play: { type: Object, required: true },
    cs_id: { type: String, required: true },
    cs_uuid: { type: String, default: null },
    disclaimer_message: { type: String, default: "" },
    pause_enabled: { type: Boolean, default: false },
    style_adjustments_enabled: { type: Boolean, default: false },
    role_play_adjustments: { type: Array, default: () => [] },
    role_play_personality: { type: String, default: "" },
    coach_api_realtime_enabled: { type: Boolean, default: false },
});

const sendMessage = (buffer) => {
    const ws = socketClient.value?.instance;
    if (ws?.readyState === WebSocket.OPEN) {
        ws.send(buffer);
    }
};

const sendJsonMessage = (data) => {
    const ws = socketClient.value?.instance;
    if (ws?.readyState === WebSocket.OPEN) {
        ws.send(JSON.stringify(data));
    }
};

const { startChat, endChat, pauseChat, resumeChat, audioLevel, audioQueue, isPlaying, isPaused, scheduleNextChunks, stopPlayback } = useRealtimeVoice({
    onMessageCallback: sendMessage,
});

const connectionState = ref("connected"); // 'connected' | 'reconnecting' | 'disconnected'

const marbleState = computed(() => {
    if (isPaused.value) {
        return "paused";
    }
    if (connectionState.value === "reconnecting") {
        return "reconnecting";
    }
    return isPlaying.value ? "speaking" : "listening";
});

// Session end dialog state
const showSessionEndDialog = ref(false);

// Time warning state
const showTimeWarning = ref(false);

// Session timer - shows warning 5 minutes before session ends.
// Timer runs continuously (even while paused) since the OpenAI session clock keeps ticking.
const { stopTimer } = useSessionTimer(
    25 * 60 * 1000, // 25 minutes (5 minutes before 30-minute session ends)
    () => {
        showTimeWarning.value = true;
    },
);

// Draggable controls
const { elRef: controlsEl, style: controlsStyle, onPointerDown: onControlsPointerDown, isDragging: controlsDragging, isPositioned: controlsPositioned } = useDraggable();

// Style adjustment state
const isStyleMenuOpen = ref(false);
const currentStyle = ref("IS_OK");

const currentStyleLabel = computed(() => {
    if (currentStyle.value === "IS_OK") {
        return role_play_personality || "As described in scenario";
    }
    const match = role_play_adjustments.find((opt) => opt[0] === currentStyle.value);
    return match ? match[1] : "Adjust style";
});

// Tracks the window between response.created and response.done where pausing would silently lose Nadia's response
const isProcessingResponse = ref(false);

// Pause / Resume
const togglePause = () => {
    if (isPaused.value) {
        resumeChat();
        sendJsonMessage({ type: "resume" });
        logUserInteraction("realtime_roleplay_resumed", {}, cs_uuid);
    } else {
        pauseChat();
        sendJsonMessage({ type: "pause" });
        logUserInteraction("realtime_roleplay_paused", {}, cs_uuid);
    }
};

// Style change
const handleStyleChange = (adjustment) => {
    currentStyle.value = adjustment;
    isStyleMenuOpen.value = false;
    sendJsonMessage({ type: "adjust_style", adjustment });
    logUserInteraction("realtime_roleplay_style_adjusted", { adjustment }, cs_uuid);
};

const handleSessionOverMaximumDuration = () => {
    showSessionEndDialog.value = true;
    // Disconnect WebSocket and end chat
    socketClient.value?.instance?.close();
    stopTimer();
    endChat();
};

const { micConsentBlocks } = useMicConsent();

const initChat = async () => {
    try {
        await startChat();
        await initializeWebSocket();
    } catch (e) {
        logError(`Error initializing chat: ${e}`);
        connectionState.value = "disconnected";
        error.value = "Failed to start chat. Please refresh the page and try again.";
    }
};

// This page opens the mic on load rather than on a click, so consent has to be settled before
// the session starts at all.
onMounted(async () => {
    if (micConsentBlocks(initChat, handleMicConsentDecline)) {
        return;
    }
    await initChat();
});

// Voice-only page: declining leaves nothing usable here.
const handleMicConsentDecline = () => {
    globalThis.history.back();
};

const error = ref(null);
const showSessionStarted = ref(false);
const hasShownSessionStarted = ref(false);
const socketClient = ref(null);

// Initialize WebSocket connection
const initializeWebSocket = async () => {
    // coach_api WebSocket endpoints require a JWT bearer subprotocol because
    // browsers cannot set Authorization headers on WebSocket upgrades.
    let subprotocols;
    if (coach_api_realtime_enabled) {
        await jwtToken.getToken();
        if (jwtToken.apiToken.value) {
            // Use a getter so reconnects always pick up the latest token value.
            subprotocols = () => (jwtToken.apiToken.value ? ["bearer", jwtToken.apiToken.value] : undefined);
        }
    }

    socketClient.value = new SocketClient(ws_url, {
        reconnectEnabled: true,
        reconnectInterval: 2000,
        maxReconnectAttempts: 10,
        subprotocols,
        // Refresh the JWT before each reconnect so a token that expired during the
        // disconnect doesn't doom every retry with a 1008 auth rejection.
        beforeReconnect: coach_api_realtime_enabled ? () => jwtToken.getToken() : null,
    });

    socketClient.value.onReconnectFailed = () => {
        stopPlayback();
        connectionState.value = "disconnected";
        error.value = "Unable to reconnect. Please refresh the page.";
    };

    socketClient.value.onOpen = () => {
        connectionState.value = "connected";
        error.value = null;
        if (!hasShownSessionStarted.value) {
            hasShownSessionStarted.value = true;
            showSessionStarted.value = true;
            setTimeout(() => {
                showSessionStarted.value = false;
            }, 5000);
        }
    };

    socketClient.value.onClose = (evt) => {
        if (evt.wasClean) {
            endChat();
            connectionState.value = "disconnected";
            error.value = "Connection closed. Please try again.";
        } else {
            stopPlayback();
            connectionState.value = "reconnecting";
            error.value = "Reconnecting...";
        }
    };

    socketClient.value.onError = createWebSocketErrorHandler({ cs_id }, "RealtimeChat", () => {
        error.value = "Connection error. Please try again.";
    });

    socketClient.value.onMessage = (msg) => {
        try {
            const data = JSON.parse(msg.data);
            if (data.type === "error") {
                error.value = data.message;
            } else if (data.type === "openai_response") {
                handleOpenAIResponse(data.data);
            } else if (data.type === "max_session_duration_reached") {
                handleSessionOverMaximumDuration();
                stopTimer();
            }
        } catch (e) {
            logError(`Error parsing WebSocket message: ${e}`);
        }
    };

    socketClient.value.connect();
};

// Handle OpenAI response
const handleOpenAIResponse = (data) => {
    if (data.type === "response.output_audio.delta") {
        return handleAudioDelta(data);
    }

    if (data.type === "input_audio_buffer.speech_started") {
        return stopPlayback();
    }

    if (data.type === "response.created") {
        isProcessingResponse.value = true;
        return;
    }

    if (data.type === "response.done") {
        isProcessingResponse.value = false;
        return;
    }
};

const handleAudioDelta = (data) => {
    try {
        if (data.delta) {
            audioQueue.value.push(data);
            scheduleNextChunks();
        }
    } catch (error) {
        logError(`Error processing audio delta: ${error}`);
        isPlaying.value = false;
    }
};

const handleEndRolePlay = () => {
    router.put(
        `/role-play/summary/${role_play.hashid}/`,
        {},
        {
            headers: {
                "X-CSRFToken": getCookie("csrftoken"),
            },
            onSuccess: () => {
                logUserInteraction("role_play_summary_sent", { id: role_play.hashid }, cs_uuid);
            },
        },
    );
};

const handleSessionEndDialogClose = () => {
    showSessionEndDialog.value = false;
    handleEndRolePlay();
};

// Clean up on component unmount
onUnmounted(async () => {
    await endChat();
    socketClient.value.removeListeners();
    socketClient.value?.instance?.close();
    socketClient.value = null;
    stopTimer();
});
</script>

<style scoped>
.background {
    background-color: #cfc5f2;
    background-image: radial-gradient(20% 60% at 100% 10%, #f5d8e2, transparent), radial-gradient(60% 20% at 20% -10%, #f5d8e2, transparent),
        radial-gradient(30% 30% at -10% 70%, #f5d8e2, transparent);
}

.slide-down-leave-active,
.slide-down-enter-active {
    transition: all 300ms ease-out;
}
.slide-down-leave-from,
.slide-down-enter-to {
    transform: translateY(0);
    opacity: 1;
}
.slide-down-enter-from,
.slide-down-leave-to {
    transform: translateY(-10%);
    opacity: 0;
}

.floating-controls {
    touch-action: none;
}
</style>
